// ═══════════════════════════════════════════════════════════════
// Out of Cycle (OOC) - Mock Data
// Replace this data with API responses when backend is ready.
// ═══════════════════════════════════════════════════════════════

import {
  OOCSubmission,
  OOCForecastAdjustment,
  OOCNDCSearchResult,
  OOCIntegrationSummary,
  OOCReferenceData,
} from 'Interfaces/FDPInterfaces/oocInterfaces';

// ─── Helper: Generate 15-month rolling periods from current month ──
// Returns ISO date strings like "2026-05-01" (day always 01).
export const generate15MonthPeriods = (): string[] => {
  const now = new Date();
  const periods: string[] = [];
  for (let i = 1; i <= 15; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    periods.push(`${yr}-${mo}-01`);
  }
  return periods;
};

// ─── Helper: Convert ISO period "2026-05-01" → display label "May 2026" ──
export const periodToLabel = (isoDate: string): string => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [yr, mo] = isoDate.split('-');
  return `${months[parseInt(mo, 10) - 1]} ${yr}`;
};

const PERIODS = generate15MonthPeriods();

// ─── Helper: build forecast cells ────────────────────────────
const fc = (
  value: number | null,
  colorType: 'modified' | 'new' | 'removed' | 'unchanged' = 'unchanged',
  previousValue: number | null = null
) => ({
  value,
  previousValue,
  colorType,
});

const buildForecasts = (
  cells: ReturnType<typeof fc>[]
) =>
  PERIODS.map((period, i) => ({
    period,
    value: cells[i]?.value ?? null,
    previousValue: cells[i]?.previousValue ?? null,
    colorType: cells[i]?.colorType ?? ('unchanged' as const),
  }));

// ═══════════════════════════════════════════════════════════════
// MAS DP Adjustments
// ═══════════════════════════════════════════════════════════════
const masAdjustments: OOCForecastAdjustment[] = [
  {
    id: 'adj-mas-001',
    oocId: 'OOC-2026-0046',
    ndc10: '51407-0481',
    ndc11: '51407-0481-12',
    supplier: 'Teva',
    molecule: 'Tetrabenazine',
    concept: 'Tetrabenazine 25 MG Oral Tab',
    packOut: '112',
    tab: 'MAS',
    forecasts: buildForecasts([
      fc(126, 'new'), ...Array(14).fill(fc(126)),
    ]),
    notes: 'FC reinstated - CMOP onboarding',
    reason: 'New customer onboarding',
    currentMonthValue: 120,
    referenceData: {
      ndc11: '51407-0481-12',
      twelveMonthSOAvg: 110,
      sixMonthSOAvg: 118,
      currentMonthPerformance: 120,
      overallMarketDemand: 130,
      newFC: 126,
      percentChange: 'NEW NDC (CMOP Onboarding)',
    },
  },
  {
    id: 'adj-mas-002',
    oocId: 'OOC-2026-0046',
    ndc10: '51407-0017',
    ndc11: '51407-0017-01',
    supplier: 'Aurobindo',
    molecule: 'Propafenone HCl',
    concept: 'Propafenone HCl 300 MG Oral Tab',
    packOut: '100',
    tab: 'MAS',
    forecasts: buildForecasts([
      fc(64, 'modified', 95), ...Array(14).fill(fc(40)),
    ]),
    notes: 'Reducing FC to maintain TMOPs MS',
    reason: 'Inability to compete with pricing',
    currentMonthValue: 50,
    referenceData: {
      ndc11: '51407-0017-01',
      twelveMonthSOAvg: 52,
      sixMonthSOAvg: 48,
      currentMonthPerformance: 50,
      overallMarketDemand: 45,
      newFC: 40,
      percentChange: '-23.1% (> 20%)',
    },
  },
  {
    id: 'adj-mas-003',
    oocId: 'OOC-2026-0046',
    ndc10: '51407-0484',
    ndc11: '51407-0484-30',
    supplier: 'Teva',
    molecule: 'Nebivolol HCl',
    concept: 'Nebivolol HCl 5 MG Oral Tab',
    packOut: '30',
    tab: 'MAS',
    forecasts: buildForecasts([
      fc(95, 'modified', 45), fc(175, 'new'), ...Array(13).fill(fc(175)),
    ]),
    notes: 'Increased demand from MTFs',
    reason: 'Re-ramp',
    currentMonthValue: 110,
    referenceData: {
      ndc11: '51407-0484-30',
      twelveMonthSOAvg: 80,
      sixMonthSOAvg: 95,
      currentMonthPerformance: 110,
      overallMarketDemand: 170,
      newFC: 175,
      percentChange: '+118.8% (> 20%)',
    },
  },
  {
    id: 'adj-mas-004',
    oocId: 'OOC-2026-0046',
    ndc10: '51407-0747',
    ndc11: '51407-0747-90',
    supplier: 'Teva',
    molecule: 'Dexlansoprazole',
    concept: 'Dexlansoprazole 60 MG DR Cap',
    packOut: '90',
    tab: 'MAS',
    forecasts: buildForecasts([
      fc(null), fc(13, 'new'), ...Array(13).fill(fc(13)),
    ]),
    notes: 'BTO to FC - small demand',
    reason: 'NDC changes - Phase-in/Phase-out',
    currentMonthValue: 12,
    referenceData: {
      ndc11: '51407-0747-90',
      twelveMonthSOAvg: 8,
      sixMonthSOAvg: 11,
      currentMonthPerformance: 12,
      overallMarketDemand: 15,
      newFC: 13,
      percentChange: 'NEW FC (BTO to FC)',
    },
  },
  {
    id: 'adj-mas-005',
    oocId: 'OOC-2026-0046',
    ndc10: '51407-0555',
    ndc11: '51407-0555-30',
    supplier: 'Teva',
    molecule: 'Atorvastatin Calcium',
    concept: 'Atorvastatin 20 MG Tab',
    packOut: '30',
    tab: 'MAS',
    forecasts: buildForecasts([
      fc(310, 'new'), ...Array(14).fill(fc(310)),
    ]),
    notes: 'Ramp to Base - 6mo tracking complete',
    reason: 'Ramp to Base',
    currentMonthValue: 310,
    referenceData: {
      ndc11: '51407-0555-30',
      twelveMonthSOAvg: 290,
      sixMonthSOAvg: 305,
      currentMonthPerformance: 310,
      overallMarketDemand: 320,
      newFC: 310,
      percentChange: 'Ramp → Base (new in MAS DP)',
    },
  },
];

// ═══════════════════════════════════════════════════════════════
// NC DP Adjustments
// ═══════════════════════════════════════════════════════════════
const ncAdjustments: OOCForecastAdjustment[] = [
  {
    id: 'adj-nc-001',
    oocId: 'OOC-2026-0046',
    ndc10: '51407-0221',
    ndc11: '51407-0221-30',
    supplier: 'Teva',
    molecule: 'Amlodipine Besylate',
    concept: 'Amlodipine 5 MG Oral Tab',
    packOut: '30',
    tab: 'NC',
    forecasts: buildForecasts([
      fc(850, 'new'), ...Array(14).fill(fc(850)),
    ]),
    notes: 'NC win - new contract starting Apr',
    reason: 'NC wins/losses',
    currentMonthValue: 0,
    referenceData: {
      ndc11: '51407-0221-30',
      twelveMonthSOAvg: 0,
      sixMonthSOAvg: 0,
      currentMonthPerformance: 0,
      overallMarketDemand: 900,
      newFC: 850,
      percentChange: 'NEW NDC (NC Win)',
    },
  },
  {
    id: 'adj-nc-002',
    oocId: 'OOC-2026-0046',
    ndc10: '51407-0331',
    ndc11: '51407-0331-60',
    supplier: 'Aurobindo',
    molecule: 'Metformin HCl',
    concept: 'Metformin HCl 500 MG Tab',
    packOut: '60',
    tab: 'NC',
    forecasts: buildForecasts(Array(15).fill(fc(0, 'removed'))),
    notes: 'NC loss - contract ended Mar-26',
    reason: 'Customer confirmed as a loss',
    currentMonthValue: 520,
    referenceData: {
      ndc11: '51407-0331-60',
      twelveMonthSOAvg: 650,
      sixMonthSOAvg: 580,
      currentMonthPerformance: 520,
      overallMarketDemand: 700,
      newFC: 0,
      percentChange: '-100% (NC Loss)',
    },
  },
  {
    id: 'adj-nc-003',
    oocId: 'OOC-2026-0046',
    ndc10: '51407-0442',
    ndc11: '51407-0442-90',
    supplier: 'Teva',
    molecule: 'Losartan Potassium',
    concept: 'Losartan 50 MG Oral Tab',
    packOut: '90',
    tab: 'NC',
    forecasts: buildForecasts([
      fc(210, 'modified', 420), ...Array(14).fill(fc(210)),
    ]),
    notes: 'NC contract end approaching - FC reduced',
    reason: 'NC wins/losses',
    currentMonthValue: 350,
    referenceData: {
      ndc11: '51407-0442-90',
      twelveMonthSOAvg: 420,
      sixMonthSOAvg: 380,
      currentMonthPerformance: 350,
      overallMarketDemand: 400,
      newFC: 210,
      percentChange: '-50.0% (> 20%)',
    },
  },
];

// ═══════════════════════════════════════════════════════════════
// Provisional MAS Adjustments  (Albuterol Sulfate — FCT_ProvisionalForecast)
// ═══════════════════════════════════════════════════════════════
const provMasAdjustments: OOCForecastAdjustment[] = [
  {
    id: 'adj-pmas-001',
    oocId: 'OOC-2026-0046',
    ndc10: '51407-0895',
    ndc11: '51407-0895-85',
    supplier: 'Teva',
    molecule: 'Albuterol Sulfate',
    concept: 'Albuterol Sulfate 0.09 MG/1 ACTUATION Inhalation Suspension',
    packOut: '10',
    tab: 'ProvMAS',
    forecasts: buildForecasts([
      fc(19709, 'new'), fc(12510), fc(12819), fc(13437),
      ...Array(11).fill(fc(13437)),
    ]),
    notes: 'New MAS launch — provisional ramp forecast',
    reason: 'Trigger for Provisional FC',
    currentMonthValue: null,
    probability: 'Medium',
  },
  {
    id: 'adj-pmas-002',
    oocId: 'OOC-2026-0046',
    ndc10: '51407-0895',
    ndc11: '51407-0895-67',
    supplier: 'Teva',
    molecule: 'Albuterol Sulfate',
    concept: 'Albuterol Sulfate 0.09 MG/1 ACTUATION Inhalation Suspension',
    packOut: '20',
    tab: 'ProvMAS',
    forecasts: buildForecasts([
      fc(0, 'new'), fc(3), fc(4), fc(4),
      ...Array(11).fill(fc(4)),
    ]),
    notes: 'Pack 20 — minimal CMOP demand, MTF/VISN only',
    reason: 'Trigger for Provisional FC',
    currentMonthValue: null,
    probability: 'Low',
  },
];

// ═══════════════════════════════════════════════════════════════
// Provisional NC Adjustments  (Albuterol Sulfate — FCT_ProvisionalForecast)
// ═══════════════════════════════════════════════════════════════
const provNcAdjustments: OOCForecastAdjustment[] = [
  {
    id: 'adj-pnc-001',
    oocId: 'OOC-2026-0046',
    ndc10: '00074-3779',
    ndc11: '00074-3779-10',
    supplier: 'AbbVie',
    molecule: 'Albuterol Sulfate',
    concept: 'Albuterol Sulfate 0.09 MG/1 ACTUATION Inhalation Suspension',
    packOut: '10',
    tab: 'ProvNC',
    forecasts: buildForecasts([
      fc(8500, 'new'), fc(12750), fc(15300),
      ...Array(12).fill(fc(15300)),
    ]),
    notes: 'NC provisional launch — 3-month ramp 50/75/90%',
    reason: 'Trigger for Provisional FC',
    currentMonthValue: null,
  },
  {
    id: 'adj-pnc-002',
    oocId: 'OOC-2026-0046',
    ndc10: '00074-3779',
    ndc11: '00074-3779-20',
    supplier: 'AbbVie',
    molecule: 'Albuterol Sulfate',
    concept: 'Albuterol Sulfate 0.09 MG/1 ACTUATION Inhalation Suspension',
    packOut: '20',
    tab: 'ProvNC',
    forecasts: buildForecasts([
      fc(656, 'new'), fc(984), fc(1181),
      ...Array(12).fill(fc(1181)),
    ]),
    notes: 'Pack 20 — smaller NCE volume',
    reason: 'Trigger for Provisional FC',
    currentMonthValue: null,
  },
];

// ═══════════════════════════════════════════════════════════════
// All OOC Submissions (Dashboard data)
// ═══════════════════════════════════════════════════════════════
export const mockOOCSubmissions: OOCSubmission[] = [
  {
    id: 'OOC-2026-0046',
    reason: 'Pack-out switch',
    submissionDate: '03/16/2026',
    submittedBy: 'Marissa Santiago',
    status: 'draft',
    planningPeriod: 'March 2026',
    contractTypes: ['MAS'],
    ndcCount: 5,
    adjustments: [...masAdjustments],
  },
  {
    id: 'OOC-2026-0045',
    reason: 'Re-ramp',
    submissionDate: '03/15/2026',
    submittedBy: 'Barbara Palau',
    status: 'draft',
    planningPeriod: 'March 2026',
    contractTypes: ['MAS', 'NC'],
    ndcCount: 3,
    adjustments: [masAdjustments[0], masAdjustments[2], ncAdjustments[2]],
  },
  {
    id: 'OOC-2026-0044',
    reason: 'NC wins/losses',
    submissionDate: '03/14/2026',
    submittedBy: 'Marissa Santiago',
    status: 'draft',
    planningPeriod: 'March 2026',
    contractTypes: ['NC', 'ProvNC'],
    ndcCount: 6,
    adjustments: [...ncAdjustments, ...provNcAdjustments],
  },
  {
    id: 'OOC-2026-0043',
    reason: 'New customer onboarding',
    submissionDate: '03/12/2026',
    submittedBy: 'Barbara Palau',
    status: 'communicated',
    planningPeriod: 'March 2026',
    contractTypes: ['MAS'],
    ndcCount: 8,
    communicatedDate: '03/13/2026',
    adjustments: [...masAdjustments, ...ncAdjustments, ...provMasAdjustments.slice(0, 2), ...provNcAdjustments],
    emailPreview: {
      to: 'SC; Purchasing; PPS; Business Development; CSE',
      subject: '3/12/26 OOC',
      attachment: 'Outofcycle_MAS_NC_DPv3.xlsx',
      bodyGreeting: 'Team,\n\nPlease see the OOC adjustments below. Feel free to reach out to me directly if you have any questions.',
      bodySignature: 'Thank you,\nMarissa Santiago | Sales Operations Analyst',
      masAdjustments: masAdjustments,
      ncAdjustments: ncAdjustments,
      provMasAdjustments: provMasAdjustments.slice(0, 2),
      provNcAdjustments: provNcAdjustments,
    },
  },
  {
    id: 'OOC-2026-0042',
    reason: 'New customer onboarding',
    submissionDate: '03/10/2026',
    submittedBy: 'Marissa Santiago',
    status: 'communicated',
    planningPeriod: 'March 2026',
    contractTypes: ['MAS', 'ProvMAS'],
    ndcCount: 11,
    communicatedDate: '03/11/2026',
    adjustments: [...masAdjustments, ...provMasAdjustments],
  },
  {
    id: 'OOC-2026-0041',
    reason: 'NC wins/losses',
    submissionDate: '03/08/2026',
    submittedBy: 'Barbara Palau',
    status: 'communicated',
    planningPeriod: 'March 2026',
    contractTypes: ['NC'],
    ndcCount: 5,
    communicatedDate: '03/09/2026',
    adjustments: [
      ...masAdjustments.slice(2, 4),
      ...ncAdjustments,
      ...provMasAdjustments.slice(0, 1),
    ],
  },
];

// ═══════════════════════════════════════════════════════════════
// History / Integrated OOC Submissions
// ═══════════════════════════════════════════════════════════════
export const mockOOCHistory: OOCIntegrationSummary[] = [
  {
    oocId: 'OOC-2026-0038',
    date: '02/18/2026',
    submittedBy: 'Marissa Santiago',
    status: 'integrated',
    reason: 'New Customer Onboarding',
    contractTypes: ['MAS'],
    ndcCount: 5,
    integratedIntoCycle: 'March 2026 DP',
    searchText: '',
  },
  {
    oocId: 'OOC-2026-0037',
    date: '02/15/2026',
    submittedBy: 'Barbara Palau',
    status: 'integrated',
    reason: 'NC Loss',
    contractTypes: ['NC'],
    ndcCount: 4,
    integratedIntoCycle: 'March 2026 DP',
    searchText: '',
  },
  {
    oocId: 'OOC-2026-0036',
    date: '02/10/2026',
    submittedBy: 'Marissa Santiago',
    status: 'integrated',
    reason: 'Pack-out Switch',
    contractTypes: ['MAS', 'ProvMAS'],
    ndcCount: 5,
    integratedIntoCycle: 'March 2026 DP',
    searchText: '',
  },
  {
    oocId: 'OOC-2026-0035',
    date: '02/05/2026',
    submittedBy: 'Barbara Palau',
    status: 'integrated',
    reason: 'Discontinuation',
    contractTypes: ['NC'],
    ndcCount: 2,
    integratedIntoCycle: 'March 2026 DP',
    searchText: '',
  },
  {
    oocId: 'OOC-2026-0034',
    date: '01/28/2026',
    submittedBy: 'Marissa Santiago',
    status: 'integrated',
    reason: 'Re-ramp',
    contractTypes: ['MAS'],
    ndcCount: 3,
    integratedIntoCycle: 'Feb 2026 DP',
    searchText: '',
  },
  {
    oocId: 'OOC-2026-0033',
    date: '01/22/2026',
    submittedBy: 'Barbara Palau',
    status: 'integrated',
    reason: 'NC Win',
    contractTypes: ['NC', 'ProvNC'],
    ndcCount: 7,
    integratedIntoCycle: 'Feb 2026 DP',
    searchText: '',
  },
];

// ═══════════════════════════════════════════════════════════════
// Historical Full Submissions (for detail modal in History screen)
// ═══════════════════════════════════════════════════════════════
export const mockHistoricalSubmissions: OOCSubmission[] = [
  {
    id: 'OOC-2026-0038',
    reason: 'New customer onboarding',
    submissionDate: '02/18/2026',
    submittedBy: 'Marissa Santiago',
    status: 'integrated',
    planningPeriod: 'February 2026',
    contractTypes: ['MAS'],
    ndcCount: 5,
    integratedDate: '03/01/2026',
    integratedIntoCycle: 'March 2026 DP',
    adjustments: masAdjustments.map((a) => ({ ...a, oocId: 'OOC-2026-0038' })),
  },
  {
    id: 'OOC-2026-0037',
    reason: 'NC wins/losses',
    submissionDate: '02/15/2026',
    submittedBy: 'Barbara Palau',
    status: 'integrated',
    planningPeriod: 'February 2026',
    contractTypes: ['NC'],
    ndcCount: 4,
    integratedDate: '03/01/2026',
    integratedIntoCycle: 'March 2026 DP',
    adjustments: ncAdjustments.map((a) => ({ ...a, oocId: 'OOC-2026-0037' })),
  },
  {
    id: 'OOC-2026-0036',
    reason: 'Pack-out switch',
    submissionDate: '02/10/2026',
    submittedBy: 'Marissa Santiago',
    status: 'integrated',
    planningPeriod: 'February 2026',
    contractTypes: ['MAS', 'ProvMAS'],
    ndcCount: 5,
    integratedDate: '03/01/2026',
    integratedIntoCycle: 'March 2026 DP',
    adjustments: [
      ...masAdjustments.slice(0, 2).map((a) => ({ ...a, oocId: 'OOC-2026-0036' })),
      ...provMasAdjustments.map((a) => ({ ...a, oocId: 'OOC-2026-0036' })),
    ],
  },
  {
    id: 'OOC-2026-0035',
    reason: 'Discontinued',
    submissionDate: '02/05/2026',
    submittedBy: 'Barbara Palau',
    status: 'integrated',
    planningPeriod: 'February 2026',
    contractTypes: ['NC'],
    ndcCount: 2,
    integratedDate: '03/01/2026',
    integratedIntoCycle: 'March 2026 DP',
    adjustments: ncAdjustments.slice(1, 3).map((a) => ({ ...a, oocId: 'OOC-2026-0035' })),
  },
  {
    id: 'OOC-2026-0034',
    reason: 'Re-ramp',
    submissionDate: '01/28/2026',
    submittedBy: 'Marissa Santiago',
    status: 'integrated',
    planningPeriod: 'January 2026',
    contractTypes: ['MAS'],
    ndcCount: 3,
    integratedDate: '02/01/2026',
    integratedIntoCycle: 'Feb 2026 DP',
    adjustments: masAdjustments.slice(0, 3).map((a) => ({ ...a, oocId: 'OOC-2026-0034' })),
  },
  {
    id: 'OOC-2026-0033',
    reason: 'NC wins/losses',
    submissionDate: '01/22/2026',
    submittedBy: 'Barbara Palau',
    status: 'integrated',
    planningPeriod: 'January 2026',
    contractTypes: ['NC', 'ProvNC'],
    ndcCount: 7,
    integratedDate: '02/01/2026',
    integratedIntoCycle: 'Feb 2026 DP',
    adjustments: [
      ...ncAdjustments.map((a) => ({ ...a, oocId: 'OOC-2026-0033' })),
      ...provNcAdjustments.map((a) => ({ ...a, oocId: 'OOC-2026-0033' })),
    ],
  },
];

// ═══════════════════════════════════════════════════════════════
// NDC Search Results (for Add NDC functionality)
// ═══════════════════════════════════════════════════════════════
export const mockNDCSearchResults: OOCNDCSearchResult[] = [
  { ndc10: '51407-0481', ndc11: '51407-0481-12', supplier: 'Teva', molecule: 'Tetrabenazine', concept: 'Tetrabenazine 25 MG Oral Tab', packOut: '112', contractType: 'MAS', planningType: 'Base', suggestedTab: 'MAS' },
  { ndc10: '51407-0017', ndc11: '51407-0017-01', supplier: 'Aurobindo', molecule: 'Propafenone HCl', concept: 'Propafenone HCl 300 MG Oral Tab', packOut: '100', contractType: 'MAS', planningType: 'Base', suggestedTab: 'MAS' },
  { ndc10: '51407-0484', ndc11: '51407-0484-30', supplier: 'Teva', molecule: 'Nebivolol HCl', concept: 'Nebivolol HCl 5 MG Oral Tab', packOut: '30', contractType: 'MAS', planningType: 'Base', suggestedTab: 'MAS' },
  { ndc10: '51407-0747', ndc11: '51407-0747-90', supplier: 'Teva', molecule: 'Dexlansoprazole', concept: 'Dexlansoprazole 60 MG DR Cap', packOut: '90', contractType: 'MAS', planningType: 'Base', suggestedTab: 'MAS' },
  { ndc10: '51407-0555', ndc11: '51407-0555-30', supplier: 'Teva', molecule: 'Atorvastatin Calcium', concept: 'Atorvastatin 20 MG Tab', packOut: '30', contractType: 'MAS', planningType: 'Ramp', suggestedTab: 'MAS' },
  { ndc10: '51407-0221', ndc11: '51407-0221-30', supplier: 'Teva', molecule: 'Amlodipine Besylate', concept: 'Amlodipine 5 MG Oral Tab', packOut: '30', contractType: 'NC', planningType: 'Base', suggestedTab: 'NC' },
  { ndc10: '51407-0331', ndc11: '51407-0331-60', supplier: 'Aurobindo', molecule: 'Metformin HCl', concept: 'Metformin HCl 500 MG Tab', packOut: '60', contractType: 'NC', planningType: 'Base', suggestedTab: 'NC' },
  { ndc10: '51407-0442', ndc11: '51407-0442-90', supplier: 'Teva', molecule: 'Losartan Potassium', concept: 'Losartan 50 MG Oral Tab', packOut: '90', contractType: 'NC', planningType: 'Base', suggestedTab: 'NC' },
  { ndc10: '51407-0895', ndc11: '51407-0895-85', supplier: 'Teva', molecule: 'Albuterol Sulfate', concept: 'Albuterol Sulfate 0.09 MG/1 ACTUATION Inhalation Suspension', packOut: '10', contractType: 'MAS', planningType: 'Provisional', suggestedTab: 'ProvMAS' },
  { ndc10: '51407-0895', ndc11: '51407-0895-67', supplier: 'Teva', molecule: 'Albuterol Sulfate', concept: 'Albuterol Sulfate 0.09 MG/1 ACTUATION Inhalation Suspension', packOut: '20', contractType: 'MAS', planningType: 'Provisional', suggestedTab: 'ProvMAS' },
  { ndc10: '00074-3779', ndc11: '00074-3779-10', supplier: 'AbbVie', molecule: 'Albuterol Sulfate', concept: 'Albuterol Sulfate 0.09 MG/1 ACTUATION Inhalation Suspension', packOut: '10', contractType: 'NC', planningType: 'Provisional', suggestedTab: 'ProvNC' },
  { ndc10: '00074-3779', ndc11: '00074-3779-20', supplier: 'AbbVie', molecule: 'Albuterol Sulfate', concept: 'Albuterol Sulfate 0.09 MG/1 ACTUATION Inhalation Suspension', packOut: '20', contractType: 'NC', planningType: 'Provisional', suggestedTab: 'ProvNC' },
];

// ═══════════════════════════════════════════════════════════════
// Reference Data for FC calculation assistance
// ═══════════════════════════════════════════════════════════════
export const mockReferenceData: Record<string, OOCReferenceData> = {
  '51407-0481-12': { ndc11: '51407-0481-12', twelveMonthSOAvg: 110, sixMonthSOAvg: 118, currentMonthPerformance: 120, overallMarketDemand: 130, newFC: 126, percentChange: 'NEW NDC (CMOP Onboarding)' },
  '51407-0017-01': { ndc11: '51407-0017-01', twelveMonthSOAvg: 52, sixMonthSOAvg: 48, currentMonthPerformance: 50, overallMarketDemand: 45, newFC: 40, percentChange: '-23.1% (> 20%)' },
  '51407-0484-30': { ndc11: '51407-0484-30', twelveMonthSOAvg: 80, sixMonthSOAvg: 95, currentMonthPerformance: 110, overallMarketDemand: 170, newFC: 175, percentChange: '+118.8% (> 20%)' },
  '51407-0555-30': { ndc11: '51407-0555-30', twelveMonthSOAvg: 290, sixMonthSOAvg: 305, currentMonthPerformance: 310, overallMarketDemand: 320, newFC: 310, percentChange: 'Ramp → Base (new in MAS DP)' },
  '51407-0221-30': { ndc11: '51407-0221-30', twelveMonthSOAvg: 0, sixMonthSOAvg: 0, currentMonthPerformance: 0, overallMarketDemand: 900, newFC: 850, percentChange: 'NEW NDC (NC Win)' },
  '51407-0442-90': { ndc11: '51407-0442-90', twelveMonthSOAvg: 420, sixMonthSOAvg: 380, currentMonthPerformance: 350, overallMarketDemand: 400, newFC: 210, percentChange: '-50.0% (> 20%)' },
};
